import CopyAllIcon from '@mui/icons-material/CopyAll';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { transactionsActions, type transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { type CashFlowTransaction, TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';
import InvoiceStatusChip from 'containers/SalesAndBilling/InvoiceStatusChip';
import { useOpenInvoice } from 'containers/SalesAndBilling/invoiceHooks';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import { DateTime } from 'luxon';
import React, { useCallback } from 'react';
import type { TxnOptionProps } from './types';
import { usePaymentOptions } from './usePaymentOptions';
import { cloneInvoiceTransaction } from 'app-components/flux/transactions/transactionsThunks';
import { InvoiceSendUpsertAction } from 'components/InvoiceSendModal/actions';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

type ResourceTxnAction = { clientId?: string; id: string; resource: CashFlowTransaction };

export const useInvoiceOptionsRaw = (
  props: TxnOptionProps & {
    /** Used if we only want to show payment related options. */
    paymentOnly?: boolean;
  },
) => {
  const { closeMenu, txnActionProps, paymentOnly, context } = props;
  const { txnToEdit: txn } = txnActionProps;
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const openInvoice = useOpenInvoice('3-dot-menu');
  const { saveTransactions } = useTxnActions({
    callback: (ret: ResourceTxnAction[]) => {
      if (ret && ret.length > 0) {
        const invoice = ret.find((t) => t.resource.type === 'INVOICE')?.resource;
        if (invoice) {
          openInvoice({ invoiceId: invoice.id ?? undefined, initialView: 'Edit' });
        }
      }
    },
  });

  const updateEstimateStatus = useCallback(
    (txn: transactionsTypes.TransactionRecord, status: transactionsTypes.EstimateStatus) => {
      closeMenu();
      let updatedTxn = txn.set('estimateStatus', status);
      if (!updatedTxn.sentOn) {
        updatedTxn = updatedTxn.set('sentOn', DateTime.local().toISODate());
      }
      dispatch(transactionsActions.updateTransaction(updatedTxn));
    },
    [dispatch, closeMenu],
  );

  const nextInvoiceNo = useAppSelector(
    (state) => businessSelectors.getBusinessForId(state, txn.businessId ?? '')?.nextInvoiceNo ?? '1000',
  );

  const salesTaxCoa = useAppSelector(
    (state) => {
      const taxAccounts = accountsSelectors.getSalesTaxAccountsByBusiness(state);
      const taxAccount = taxAccounts?.get(txn.businessId ?? '') || null;
      return chartOfAccountsTypes.mkChartOfAccount({ type: 'ACCOUNT', id: taxAccount?.id ?? '' });
    },
    (prev, next) => prev.id === next.id && prev.type === next.type,
  );

  const options: any[] = usePaymentOptions(props) ?? [];

  const subtypeOptions = [];

  if (txn.subtype === 'ESTIMATE') {
    subtypeOptions.push({
      id: 'status-update',
      key: 'status-update',
      label: 'Update status',
      startIcon: <EditNoteOutlinedIcon />,
      subMenu: [
        {
          id: 'status-update-accepted',
          key: 'status-update-accepted',
          label: '',
          startIcon: <InvoiceStatusChip value='ACCEPTED' />,
          onSelect: () => {
            updateEstimateStatus(txn, 'ACCEPTED');
          },
        },
        {
          id: 'status-update-declined',
          key: 'status-update-declined',
          label: '',
          startIcon: <InvoiceStatusChip value='DECLINED' />,
          onSelect: () => {
            updateEstimateStatus(txn, 'DECLINED');
          },
        },
      ],
    });
    subtypeOptions.push({
      id: 'invoice-convert',
      key: 'invoice-convert',
      startIcon: <CopyAllIcon />,
      label: 'Generate invoice',
      onSelect: () => {
        closeMenu();
        const clonedTxn = transactionsUtils.convertEstimateToInvoiceTransaction(txn, salesTaxCoa, {
          invoiceNo: `${nextInvoiceNo ?? '1000'}`,
          payee: `Invoice ${nextInvoiceNo ?? '1000'}`,
          subtype: TransactionSubType.Invoice,
          postedOn: DateTime.local().toISODate(),
          sentOn: '',
          paymentMethods: accountsSelectors.stripeAccountPaymentMethods(store.getState(), txn.businessId),
        });
        if (clonedTxn) {
          saveTransactions([clonedTxn]);
        }
      },
    });
  }

  subtypeOptions.push({
    id: 'invoice-clone',
    key: 'invoice-clone',
    startIcon: <FileCopyIcon />,
    label: `Duplicate ${transactionsUtils.invoiceSubtypeTitle(txn.subtype).toLowerCase()}`,
    onSelect: async () => {
      closeMenu();
      const invoiceId = await dispatch(cloneInvoiceTransaction({ invoice: txn })).unwrap();
      if (invoiceId) {
        openInvoice({ invoiceId, initialView: 'Edit' });
      }
    },
  });

  if (txn.subtype === 'SALES_RECEIPT' && !paymentOnly && !txn.sentOn) {
    subtypeOptions.push({
      label: 'Send via email',
      startIcon: <EmailOutlinedIcon />,
      onSelect: () => {
        closeMenu();
        dispatch(
          InvoiceSendUpsertAction({
            context,
            formComponentProps: { invoice: txn },
          }),
        );
      },
    });
  }

  options.push(...subtypeOptions);
  if (options.length > 0 && !paymentOnly) {
    options[options.length - 1].divider = true;
  }
  return options;
};
